/**
 * The canvas.
 *
 * One transform on a single "world" layer moves every table at once, so panning
 * and zooming stay smooth regardless of table count — the browser composites a
 * single layer instead of re-laying-out hundreds of nodes.
 *
 * The world is a *room*, not an unbounded plane: tables cannot be dragged out
 * of it and the camera cannot be panned off it. A finite floor is what a venue
 * actually is, and it also means "where did my table go" stops being a question
 * the editor can be asked.
 *
 * On a touch screen one finger pans the floor or moves what it lands on, and two
 * fingers pinch and pan the camera from anywhere — a finger resting on a table
 * included. A finger on the floor is only a tap once it lifts without having
 * travelled: every pan starts on the floor, and clearing the selection at the
 * first touch meant nobody could look around the table they were editing.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'

import { boxesInRect } from '../lib/arrange'
import { rotatedHalfExtent, tableBounds, unionBounds } from '../lib/geometry'
import { stopGlide } from '../lib/glide'
import { useT } from '../lib/i18n/I18nProvider'
import { type PinchStart, pinchCamera } from '../lib/pinch'
import type { Plan } from '../lib/types'
import { floorBounds, usePlanStore } from '../store/planStore'
import {
  GRID_SIZE,
  MAX_ZOOM,
  minZoomFor,
  screenToWorld,
  tapsSuppressed,
  useViewStore,
} from '../store/viewStore'
import { TableNode } from './TableNode'

interface Props {
  plan: Plan
}

interface PanSession {
  pointerId: number
  pointerType: string
  startX: number
  startY: number
  originX: number
  originY: number
  moved: boolean
}

interface MarqueeSession {
  pointerId: number
  /** Container-local screen coordinates — the rectangle is chrome, not world. */
  startX: number
  startY: number
  /** What was selected when the drag began; Shift means add, never replace. */
  base: string[]
}

/** The two fingers a pinch follows, and where they and the camera started. */
interface Pinch extends PinchStart {
  ids: [number, number]
}

/** Below this the gesture was a click, not a box. */
const MARQUEE_THRESHOLD_PX = 3

/** Two floor taps this close, in time and on screen, are a touch screen's double-click. */
const DOUBLE_TAP_MS = 320
const DOUBLE_TAP_PX = 24

/** Smaller than this, the canvas is hidden behind a full-height sheet — not a size to clamp to. */
const MIN_STAGE_PX = 80

export function CanvasStage({ plan }: Props) {
  const t = useT()
  const containerRef = useRef<HTMLDivElement>(null)
  const panRef = useRef<PanSession | null>(null)
  const marqueeRef = useRef<MarqueeSession | null>(null)
  // The rectangle is painted by writing to this node's style directly. Holding
  // it in React state would re-render the stage — and every table on it — on
  // every pointer-move, which is the one thing the world layer exists to avoid.
  const marqueeBoxRef = useRef<HTMLDivElement>(null)
  const touchesRef = useRef(new Map<number, { x: number; y: number }>())
  const pinchRef = useRef<Pinch | null>(null)
  const lastFloorTapRef = useRef<{ at: number; x: number; y: number } | null>(null)
  const lastPointerTypeRef = useRef('mouse')

  // Separate primitive selectors: returning an object literal here would create
  // a new reference every render and loop forever under zustand's Object.is check.
  const cameraX = useViewStore((s) => s.x)
  const cameraY = useViewStore((s) => s.y)
  const zoom = useViewStore((s) => s.zoom)
  const showGrid = useViewStore((s) => s.showGrid)
  const isPanning = useViewStore((s) => s.isPanning)
  const dragging = useViewStore((s) => s.drag !== null)
  const placing = useViewStore((s) => s.placing?.kind ?? null)
  const compact = useViewStore((s) => s.compact)
  const clearSelection = usePlanStore((s) => s.clearSelection)

  const size = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect()
    return { width: rect?.width ?? 1200, height: rect?.height ?? 800 }
  }, [])

  // --- wheel: zoom at cursor -------------------------------------------------
  // Registered manually because React's onWheel is passive, and a passive
  // listener cannot preventDefault the browser's page zoom / back-swipe.
  useEffect(() => {
    const element = containerRef.current
    if (!element) return undefined

    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      stopGlide()
      const rect = element.getBoundingClientRect()
      const px = event.clientX - rect.left
      const py = event.clientY - rect.top
      const store = useViewStore.getState()

      // A trackpad pinch arrives as ctrl+wheel; two-finger scroll has a
      // horizontal component. Treat the former as zoom, the latter as pan, and
      // a plain vertical wheel (a mouse) as zoom — which is what people expect
      // from a floor-plan tool.
      if (!event.ctrlKey && !event.metaKey && Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        store.panBy(-event.deltaX, -event.deltaY)
        return
      }
      if (event.shiftKey && !event.ctrlKey && !event.metaKey) {
        store.panBy(-event.deltaY, 0)
        return
      }
      const intensity = event.ctrlKey || event.metaKey ? 0.01 : 0.0022
      store.zoomAt(Math.exp(-event.deltaY * intensity), px, py)
    }

    element.addEventListener('wheel', onWheel, { passive: false })
    return () => element.removeEventListener('wheel', onWheel)
  }, [])

  // --- the floor, and how far the camera may roam ---------------------------
  // The hall, widened to take in anything sitting outside it — an imported
  // venue bigger than the standard room, or a plan saved before the room
  // existed. One union drives everything: the white floor drawn below, the
  // drag limits in the store, and the camera clamp.
  const roam = useMemo(() => floorBounds(plan.tables), [plan.tables])
  const roamRef = useRef(roam)
  roamRef.current = roam

  /** Hand the store the floor and the canvas size — unless a full-height sheet has hidden the canvas. */
  const syncRoam = useCallback(() => {
    const next = size()
    // Clamping the camera against a canvas of no size would throw the room
    // somewhere odd for when the sheet goes back down.
    if (next.width < MIN_STAGE_PX || next.height < MIN_STAGE_PX) return
    useViewStore.getState().setRoam(roamRef.current, next)
  }, [size])

  useEffect(() => {
    syncRoam()
  }, [roam, syncRoam])

  // Keep the clamp honest whenever the canvas changes shape: a window resized,
  // a phone turned, a banner above it, or the compact editor's sheet opening
  // under it (which is how the sheet keeps every "middle of the view" right).
  // One observer for the editor's life — the floor changes on every drag frame.
  useEffect(() => {
    const element = containerRef.current
    if (!element) return undefined
    const observer = new ResizeObserver(syncRoam)
    observer.observe(element)
    return () => observer.disconnect()
  }, [syncRoam])

  // --- fit the plan on first open -------------------------------------------
  useEffect(() => {
    const bounds = unionBounds(
      plan.tables.map((table) =>
        tableBounds(table.shape, table.seatCount, table.x, table.y, table.rotation),
      ),
    )
    const stored = plan.viewport
    if (stored && (stored.x !== 0 || stored.y !== 0 || stored.zoom !== 1)) {
      useViewStore.getState().resetCamera(stored)
    } else if (bounds) {
      useViewStore.getState().fitTo(bounds, size())
    } else {
      const { width, height } = size()
      useViewStore.getState().resetCamera({ x: width / 2, y: height / 2, zoom: 1 })
    }
    // Only when the plan identity changes — not on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.id])

  // --- two fingers -------------------------------------------------------------

  const toStage = (point: { x: number; y: number }) => {
    const rect = containerRef.current?.getBoundingClientRect()
    return rect ? { x: point.x - rect.left, y: point.y - rect.top } : point
  }

  const startPinch = () => {
    const [[idA, a], [idB, b]] = [...touchesRef.current.entries()]
    const view = useViewStore.getState()
    pinchRef.current = {
      ids: [idA, idB],
      camera: { x: view.x, y: view.y, zoom: view.zoom },
      a: toStage(a),
      b: toStage(b),
    }
    // Whatever the first finger had begun gives way to the camera.
    panRef.current = null
    if (marqueeRef.current) {
      marqueeRef.current = null
      if (marqueeBoxRef.current) marqueeBoxRef.current.style.display = 'none'
    }
    view.setPanning(false)
    view.endDrag()
    view.setPinching(true)
  }

  // Capture phase: a finger that landed on a table is still a finger of the pinch.
  const onPointerDownCapture = (event: React.PointerEvent<HTMLDivElement>) => {
    lastPointerTypeRef.current = event.pointerType
    stopGlide()
    if (event.pointerType !== 'touch') return
    touchesRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (touchesRef.current.size === 2 && !pinchRef.current) startPinch()
  }

  const onPointerMoveCapture = (event: React.PointerEvent<HTMLDivElement>) => {
    const touches = touchesRef.current
    if (!touches.has(event.pointerId)) return
    touches.set(event.pointerId, { x: event.clientX, y: event.clientY })
    const pinch = pinchRef.current
    const a = pinch && touches.get(pinch.ids[0])
    const b = pinch && touches.get(pinch.ids[1])
    if (!pinch || !a || !b) return
    const view = useViewStore.getState()
    view.resetCamera(
      pinchCamera(pinch, toStage(a), toStage(b), { minZoom: minZoomFor(view), maxZoom: MAX_ZOOM }),
    )
  }

  // Lifts are watched on the window: a finger can leave the canvas before it
  // lets go, and a finger forgotten here would make the next touch a "pinch".
  useEffect(() => {
    const release = (event: PointerEvent) => {
      const touches = touchesRef.current
      if (!touches.delete(event.pointerId)) return
      if (pinchRef.current?.ids.includes(event.pointerId)) pinchRef.current = null
      // The camera keeps the fingers until the last one lifts, so a pinch that
      // ends one finger at a time never becomes a drag of what the other rests on.
      if (touches.size === 0 && useViewStore.getState().pinching) {
        useViewStore.getState().setPinching(false)
      }
    }
    window.addEventListener('pointerup', release, true)
    window.addEventListener('pointercancel', release, true)
    return () => {
      window.removeEventListener('pointerup', release, true)
      window.removeEventListener('pointercancel', release, true)
    }
  }, [])

  // --- pan by dragging empty canvas -----------------------------------------

  /** Paint the rectangle straight onto the DOM — see `marqueeBoxRef`. */
  const paintMarquee = (x0: number, y0: number, x1: number, y1: number) => {
    const node = marqueeBoxRef.current
    if (!node) return
    const width = Math.abs(x1 - x0)
    const height = Math.abs(y1 - y0)
    // Nothing to show until the drag has outgrown a click.
    node.style.display =
      width < MARQUEE_THRESHOLD_PX && height < MARQUEE_THRESHOLD_PX ? 'none' : 'block'
    node.style.left = `${Math.min(x0, x1)}px`
    node.style.top = `${Math.min(y0, y1)}px`
    node.style.width = `${width}px`
    node.style.height = `${height}px`
  }

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const isBackground = event.target === event.currentTarget ||
      (event.target as HTMLElement).dataset.canvasBackground === 'true'
    const wantsPan = event.button === 1 || event.button === 0

    if (!isBackground || !wantsPan) return
    const view = useViewStore.getState()
    if (view.pinching) return

    // Shift on the background draws a selection box. Plain drag still pans, so
    // the gesture every existing plan was built with is untouched, and Shift
    // means the same thing here as it does on a table: add to the selection.
    // Select mode is Shift for a touch screen.
    const rect = containerRef.current?.getBoundingClientRect()
    if (event.button === 0 && (event.shiftKey || view.selectMode) && rect) {
      const startX = event.clientX - rect.left
      const startY = event.clientY - rect.top
      event.currentTarget.setPointerCapture(event.pointerId)
      marqueeRef.current = {
        pointerId: event.pointerId,
        startX,
        startY,
        base: usePlanStore.getState().selectedTableIds,
      }
      paintMarquee(startX, startY, startX, startY)
      return
    }

    // A mouse press on the floor deselects at once, as it always has. A finger
    // waits to learn whether it is a tap or the start of a pan.
    if (event.button === 0 && event.pointerType === 'mouse') clearSelection()

    event.currentTarget.setPointerCapture(event.pointerId)
    panRef.current = {
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      startX: event.clientX,
      startY: event.clientY,
      originX: view.x,
      originY: view.y,
      moved: false,
    }
    view.setPanning(true)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const marquee = marqueeRef.current
    if (marquee && marquee.pointerId === event.pointerId) {
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      paintMarquee(marquee.startX, marquee.startY, event.clientX - rect.left, event.clientY - rect.top)
      return
    }

    const session = panRef.current
    if (!session || session.pointerId !== event.pointerId) return
    const dx = event.clientX - session.startX
    const dy = event.clientY - session.startY
    // A fingertip wobbles more than a mouse does before it means to move.
    const slop = session.pointerType === 'mouse' ? 3 : 8
    if (!session.moved && Math.hypot(dx, dy) < slop) return
    session.moved = true
    useViewStore.getState().setCamera({ x: session.originX + dx, y: session.originY + dy })
  }

  const endGesture = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const marquee = marqueeRef.current
    if (marquee && marquee.pointerId === event.pointerId) {
      marqueeRef.current = null
      if (marqueeBoxRef.current) marqueeBoxRef.current.style.display = 'none'
      commitMarquee(marquee, event)
      return
    }

    const session = panRef.current
    if (!session || session.pointerId !== event.pointerId) return
    panRef.current = null
    useViewStore.getState().setPanning(false)
    if (!session.moved && event.type === 'pointerup') tapFloor(event, session.pointerType)
  }

  /**
   * A tap on empty floor: let go of the selection, put the sheet away, and on a
   * touch screen watch for the second tap of a double-tap.
   */
  const tapFloor = (event: React.PointerEvent<HTMLDivElement>, pointerType: string) => {
    const view = useViewStore.getState()
    if (tapsSuppressed(view)) return
    // Missing a chair by a few pixels while seating someone must not throw the
    // placement away; the banner has its own Cancel.
    if (view.placing) return

    if (pointerType !== 'mouse') {
      clearSelection()
      const now = performance.now()
      const last = lastFloorTapRef.current
      if (
        last &&
        now - last.at < DOUBLE_TAP_MS &&
        Math.hypot(event.clientX - last.x, event.clientY - last.y) < DOUBLE_TAP_PX
      ) {
        lastFloorTapRef.current = null
        dropRoundTable(event.clientX, event.clientY)
        return
      }
      lastFloorTapRef.current = { at: now, x: event.clientX, y: event.clientY }
    }
    if (view.compact) view.closePanel()
  }

  /**
   * Turn the rectangle into a selection.
   *
   * The box is screen-space, so both corners go back through the camera before
   * anything is tested, and a table counts as caught when its **chair
   * envelope** touches the box — the chairs are as much the table as the top
   * is, and a planner lassoing a row expects the row.
   */
  const commitMarquee = (
    marquee: MarqueeSession,
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    const endX = event.clientX - rect.left
    const endY = event.clientY - rect.top
    // A Shift-click on empty canvas is not an empty box — leave the selection be.
    if (
      Math.abs(endX - marquee.startX) < MARQUEE_THRESHOLD_PX &&
      Math.abs(endY - marquee.startY) < MARQUEE_THRESHOLD_PX
    ) {
      return
    }

    const view = useViewStore.getState()
    const from = screenToWorld(view, marquee.startX, marquee.startY)
    const to = screenToWorld(view, endX, endY)
    const store = usePlanStore.getState()
    const boxes = (store.plan?.tables ?? []).map((table) => {
      const half = rotatedHalfExtent(table.shape, table.seatCount, table.rotation)
      return { id: table.id, x: table.x, y: table.y, halfX: half.x, halfY: half.y }
    })

    const caught = boxesInRect(boxes, {
      minX: Math.min(from.x, to.x),
      maxX: Math.max(from.x, to.x),
      minY: Math.min(from.y, to.y),
      maxY: Math.max(from.y, to.y),
    })
    store.selectTables([...new Set([...marquee.base, ...caught])])
  }

  // --- double-click empty space to drop a table ------------------------------

  const dropRoundTable = (clientX: number, clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    const world = screenToWorld(useViewStore.getState(), clientX - rect.left, clientY - rect.top)
    usePlanStore.getState().addTable('round', world)
  }

  const onDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (target.dataset.canvasBackground !== 'true' && event.target !== event.currentTarget) return
    // A finger's double-tap is counted in `tapFloor`; some browsers also report
    // it as a double-click, which would drop a second table on the first.
    if (lastPointerTypeRef.current !== 'mouse') return
    dropRoundTable(event.clientX, event.clientY)
  }

  const sortedTables = useMemo(
    () => [...plan.tables].sort((a, b) => a.zIndex - b.zIndex),
    [plan.tables],
  )
  // One lookup for every table, rebuilt only when the guest list itself changes
  // (edits keep its identity otherwise — see lib/share.ts).
  const guestById = useMemo(
    () => new Map(plan.guests.map((guest) => [guest.id, guest])),
    [plan.guests],
  )

  // The dot grid lives in background-image space so it costs nothing to scroll.
  const gridStyle = showGrid
    ? {
        backgroundImage: 'radial-gradient(circle, var(--grid-dot) 1px, transparent 1px)',
        backgroundSize: `${GRID_SIZE * zoom}px ${GRID_SIZE * zoom}px`,
        backgroundPosition: `${cameraX}px ${cameraY}px`,
      }
    : undefined

  return (
    <div
      ref={containerRef}
      className={`stage${isPanning ? ' is-panning' : ''}${dragging ? ' is-dropping' : ''}${placing === 'guest' ? ' is-placing' : ''}${placing === 'swap' ? ' is-swapping' : ''}`}
      data-canvas-background="true"
      onPointerDownCapture={onPointerDownCapture}
      onPointerMoveCapture={onPointerMoveCapture}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
      onDoubleClick={onDoubleClick}
      style={gridStyle}
      role="application"
      aria-label={t.canvas.label}
    >
      <div
        className="stage-world"
        style={{ transform: `translate3d(${cameraX}px, ${cameraY}px, 0) scale(${zoom})` }}
      >
        {/* The floor. Inside the transformed layer so it pans and zooms with
            the tables — it is part of the world, not chrome drawn over it.
            Drawn from the same union as the drag limits, so every table always
            stands on the white. */}
        <div
          className="stage-room"
          aria-hidden="true"
          style={{
            left: roam.minX,
            top: roam.minY,
            width: roam.maxX - roam.minX,
            height: roam.maxY - roam.minY,
          }}
        />
        {sortedTables.map((table) => (
          <TableNode
            key={table.id}
            table={table}
            guestById={guestById}
            seatLabels={plan.settings.seatLabels}
            containerRef={containerRef}
          />
        ))}
      </div>

      {/* Chrome, not world: the box is drawn in screen pixels so it keeps a
          1px border at any zoom, and it never scales with the room. */}
      <div className="stage-marquee" ref={marqueeBoxRef} aria-hidden="true" />

      {plan.tables.length === 0 && (
        <div className="stage-empty" data-canvas-background="true">
          <h3>{t.canvas.emptyTitle}</h3>
          <p>{compact ? t.canvas.emptyBodyTouch : t.canvas.emptyBody}</p>
        </div>
      )}
    </div>
  )
}
